import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
} from '@mui/material';
import useCoreApi from '../Utils/useCoreApi';

const AssessmentQuestions = () => {
  const navigate = useNavigate();
  const coreApi = useCoreApi();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const subjectId = sessionStorage.getItem('selected_subject_id');
  const startYear = sessionStorage.getItem('start_year') || 3;

  useEffect(() => {
    if (!subjectId) return;

    coreApi
      .get(`/assessment/generate`, {
        params: {
          subject_id: subjectId,
          start_year: startYear,
        },
      })
      .then((res) => {
        setQuestions(res.data.questions);
      })
      .catch((err) => {
        console.error("Error fetching assessment questions:", err);
      });
  }, [subjectId, startYear]);

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    try {
      const userId = localStorage.getItem('user_id');
  
      const payload = {
        user_id: Number(userId),
        subject_id: Number(subjectId),
        answers,
      };
  
      const response = await coreApi.post('/assessment/submit', payload);
  
      const { suggested_year_level, score, total, answers: enrichedAnswers, bandPerformance } = response.data;
  
      navigate('/assessment-result', {
        state: {
          suggestedYearLevel: suggested_year_level,
          score,
          total,
          answers: enrichedAnswers,
          bandPerformance, // 👈 include this
        },
      });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert(
        'Oops! Something went wrong while submitting your answers. Please try again or contact support.'
      );
    }
  };
  

  // Handle loading and safe rendering
  if (questions.length === 0 || !questions[currentQuestionIndex]) {
    return (
      <Typography variant="h6" style={{ marginTop: '20px' }}>
        Loading questions...
      </Typography>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = answers[currentQuestion.id];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '20px',
        padding: '0 20px',
      }}
    >
      <div style={{ maxWidth: '800px', width: '100%', marginBottom: '20px' }}>
        <Typography variant="body1" color="textSecondary">
          It's okay if you don't know some (or any!) of the answers. 
          This assessment helps us understand where you're at 
          to provide the best learning experience for you.
        </Typography>
      </div>
  
      <div style={{ maxWidth: '800px', width: '100%' }}>
        <Typography variant="h5" gutterBottom>
          Question {currentQuestionIndex + 1} of {questions.length}
        </Typography>
  
        <Card variant="outlined" sx={{ marginBottom: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {currentQuestion.question_text}
            </Typography>
  
            <Grid container spacing={2}>
              {currentQuestion.options.map((option, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    variant="outlined"
                    style={{
                      borderColor:
                        selectedAnswer === option ? '#1976d2' : 'rgba(0, 0, 0, 0.12)',
                      backgroundColor:
                        selectedAnswer === option ? '#e3f2fd' : 'inherit',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                  >
                    <CardContent>
                      <Typography variant="body1">{option}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
  
            <div style={{ marginTop: '20px' }}>
              <Button
                variant="outlined"
                onClick={() => handleAnswerSelect(currentQuestion.id, "I don't know")}
                style={{ textTransform: 'none' }}
              >
                I don't know
              </Button>
            </div>
  
            <div
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
              >
                Back
              </Button>
  
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (!selectedAnswer) {
                    handleAnswerSelect(currentQuestion.id, "I don't know");
                  }
                  handleNextQuestion();
                }}
              >
                {currentQuestionIndex < questions.length - 1
                  ? 'Next Question'
                  : 'Submit Assessment'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
  
  
};

export default AssessmentQuestions;
